/**
 * Premium / Subscription types + the premium page's locked content (PPL-352).
 *
 * The static copy below ships VERBATIM from the design kit
 * (design project pplkpr — premium-kit.jsx). The vessel is the design work:
 * tier table rows, FAQ text and hero lines are locked copy, not
 * placeholders. What is NOT locked is any contextual value — prices, the lapsed
 * paused-count, trial day, renewal dates, and the served-config numbers (the
 * free remembered-people limit and the memory window) — those render from live
 * data through the template functions below, with the canvas numbers (5, 30)
 * kept only as display defaults while nothing is served.
 *
 * Wording contract (same as rememberService): "f8 remembers", never "track"
 * and never "keep".
 *
 * PPL-482 — ONE memory vocabulary, and it is "remember". "remembers" is the
 * ACTIVE meter (the ppl f8 actively remembers); everyone else is "paused" —
 * archived, preserved, not lost, and never described as "remembered" or as
 * deleted. keep / kept / keeping up / unkeep are out of this register, so the
 * kit lines that used them are overridden below and the kit quotes in their
 * comments carry the shipped wording, not the canvas's.
 */

export type PlanTier = 'free' | 'premium';

export type PlanType = 'monthly' | 'yearly';
export type PlanStatus = 'active' | 'cancelled' | 'expired';

export interface PremiumStatus {
  isPremium: boolean;
  plan: PlanTier;
  selectedPlan?: string;         // from users/{uid}.selectedPlan — the authoritative entitlement (PPL-7)
  planType?: PlanType;           // monthly or yearly
  planStatus?: PlanStatus;       // active, cancelled, or expired
  /** Stamped server-side when a subscription is cancelled (kept active until expiry). */
  cancelledAt?: any;
  expiry?: any;                  // Firestore Timestamp
  store?: string;                // 'app_store' | 'play_store' | 'stripe' | 'web' | null (older docs uppercase)
  transactionId?: string;
  /**
   * PPL-357 trial fields, stamped server-side on the user doc. `trialStatus`
   * is 'active' while a full-memory trial runs; `trialStartedAt`/`trialEndsAt`
   * bound it. All may be absent — no trialists exist yet, so every consumer
   * treats them as optional and validates the timestamps before trusting them.
   */
  trialStatus?: string | null;
  trialStartedAt?: any;          // Firestore Timestamp | ms | ISO
  trialEndsAt?: any;             // Firestore Timestamp | ms | ISO
  /** Stamped server-side when a premium plan actually lapsed (PPL-350). */
  lapsedAt?: any;
}

/**
 * The two USD prices, raw. **Chosen, not computed** — see
 * premiumService.getPricing for why USD is the price and ZAR is the charge.
 * Everything the page displays (per-month equivalent, savings %) derives from
 * these two numbers at render time so no third copy of a price can exist.
 */
export interface WebPricing {
  usdMonthly: number;
  usdYearly: number;
}

/* ------------------------------------------------------------------ *
 * Served-config display defaults
 *
 * The free remembered-people limit and the memory window are Firestore config
 * served by getRememberedPeopleQuotaEurW — never numbers this file knows. The
 * canvas values (5, 30) survive only as display defaults, used while the quota
 * has not loaded. PPL-383 serves `freeLimit`/`freeMemoryWindowDays` to
 * everyone — entitled users included — so callers read those first and fall
 * back through the per-user fields to these constants.
 * ------------------------------------------------------------------ */

export const FREE_LIMIT_FALLBACK = 5;
export const MEMORY_WINDOW_FALLBACK_DAYS = 30;

/** The PPL-357 trial length shown while trialStartedAt is absent. */
export const DEFAULT_TRIAL_LENGTH_DAYS = 14;

export interface MemoryConfig {
  freeLimit: number;
  windowDays: number;
}

export const DEFAULT_MEMORY_CONFIG: MemoryConfig = {
  freeLimit: FREE_LIMIT_FALLBACK,
  windowDays: MEMORY_WINDOW_FALLBACK_DAYS,
};

/* ------------------------------------------------------------------ *
 * Locked content — premium-kit.jsx; numeric config renders from MemoryConfig
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Voice — the numbers are served, never written (PPL-354, PPL-515)
 * ------------------------------------------------------------------ */

/** Display fallbacks only; the real values come from getVoiceChatQuotaEurW. */
export const VOICE_FREE_SESSIONS_FALLBACK = 3;
export const VOICE_FREE_SESSION_MINUTES_FALLBACK = 10;
export const VOICE_PREMIUM_MINUTES_FALLBACK = 60;

/** What `getVoiceChatQuotaEurW` returns, as far as this copy needs it. */
export interface VoiceQuotaSnapshot {
  tier?: string | null;
  /** Free: sessions (lifetime model) or min/mo (monthly model). Premium: min/mo. */
  limit?: number | null;
  /** PPL-354 shape: 'lifetime' | 'monthly'. Absent today (lifetime implied). */
  model?: string | null;
  maxSessionMinutes?: number | null;
  premiumMonthlyMinutes?: number | null;
}

export interface VoiceLabels {
  free: string;
  premium: string;
  highlight: string;
}

/**
 * The app's `voiceLabels` (src/utils/premiumContent.ts), ported verbatim so
 * the two surfaces cannot drift: web used to hard-code "60 min / mo" in three
 * places while the app read the served allowance.
 */
export function voiceLabels(v: VoiceQuotaSnapshot | null): VoiceLabels {
  const num = (x: unknown): number | null =>
    typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : null;

  // The free allowance is only knowable from a FREE caller's snapshot — a
  // premium caller's `limit` is their own monthly minutes.
  const freeLimit = v && v.tier === 'free' ? num(v.limit) : null;
  const sessionMinutes = num(v?.maxSessionMinutes) ?? VOICE_FREE_SESSION_MINUTES_FALLBACK;
  const free = v?.model === 'monthly' && freeLimit !== null
    ? `${freeLimit} min / mo`
    : `${freeLimit ?? VOICE_FREE_SESSIONS_FALLBACK} sessions (${sessionMinutes} min each)`;

  const premiumMinutes = num(v?.premiumMonthlyMinutes)
    ?? (v && v.tier === 'premium' ? num(v.limit) : null)
    ?? VOICE_PREMIUM_MINUTES_FALLBACK;

  return {
    free,
    premium: `${premiumMinutes} min / mo`,
    highlight: `${premiumMinutes} voice min / mo`,
  };
}

export const DEFAULT_VOICE_LABELS: VoiceLabels = voiceLabels(null);

/**
 * The free meter's own sentence. The unit is served: under the lifetime model
 * `limit` counts SESSIONS, under PPL-354's monthly model it counts MINUTES,
 * and printing "sessions" either way is how the page ends up saying
 * "2 / 5 free sessions used" about minutes.
 */
export function voiceMeterLine(v: VoiceQuotaSnapshot | null, used: number): string {
  const limit = typeof v?.limit === 'number' && v.limit > 0 ? v.limit : null;
  if (limit === null) return '';
  const unit = v?.model === 'monthly' ? 'free min used' : 'free sessions used';
  return `${used} / ${limit} ${unit}`;
}

export const PREM_HERO = {
  h1a: 'f8 remembers',
  h1b: 'everyone. forever.',
} as const;

/**
 * Hero sub-line.
 *
 * PPL-515: this page sells premium, it does not explain free. The old line
 * spent its whole length describing what the free plan cannot do — the reader
 * is already here, so tell them what they get. The comparison table below is
 * where the two plans are set against each other, once.
 *
 * No served numbers on purpose: the concrete figures belong in the table,
 * where they are labelled.
 */
export function heroSub(): string {
  return 'every person you meet, remembered for good — a brief before you see them, and a history that never fades.';
}

export interface TierRow {
  f: string;
  free: string;
  prem: string;
  /** Identical on both tiers — rendered muted (0.55), never hidden. */
  same?: boolean;
  /** A memory row — the reason premium exists. */
  hi?: boolean;
}

/** The tier table — 11 rows; free=premium rows muted, never hidden. */
export function premTierRows(cfg: MemoryConfig, voice: VoiceLabels): TierRow[] {
  return [
    { f: 'f8 text chat', free: 'unlimited', prem: 'unlimited', same: true },
    { f: 'contact + calendar sync', free: 'unlimited', prem: 'unlimited', same: true },
    { f: 'people f8 remembers', free: `${cfg.freeLimit}`, prem: 'everyone', hi: true },
    { f: 'pre-meet briefs', free: `the ${cfg.freeLimit} f8 remembers`, prem: 'every event', hi: true },
    { f: 'proactive nudges', free: '—', prem: '✓', hi: true },
    // Enforced values; PPL-354 moves these to 5 min/mo refreshing + 120.
    { f: 'voice chat', free: voice.free, prem: voice.premium },
    { f: 'timeline history', free: `${cfg.windowDays} days`, prem: 'forever', hi: true },
    { f: 'AI-drafted messages', free: '10 lifetime', prem: 'unlimited' },
    { f: 'profile viewers', free: '—', prem: '✓' },
    { f: 'ads', free: 'public feed only', prem: 'none' },
    { f: 'circles, rooms, posts, discovery', free: 'unlimited', prem: 'unlimited', same: true },
  ];
}

/** What the muted rows mean — a note about the table, not a pitch for free. */
export const PREM_TABLE_FOOT = 'rows that match are the same on both plans.';

export interface PremFaqItem {
  q: string;
  a: string;
  /** Gradient hero line above the answer. */
  hero?: string;
  /** Open by default. */
  open?: boolean;
  /** Only rendered while trialing. */
  trialOnly?: boolean;
}

export function premFaqs(cfg: MemoryConfig, trialLength: number): PremFaqItem[] {
  return [
    { q: 'what happens if i downgrade?', hero: 'f8 never forgets anyone.', a: `downgrading pauses, never deletes. f8 remembers your ${cfg.freeLimit} most recent ppl, ${cfg.windowDays} days at a time — everyone else is paused, not lost. everything comes back the moment you return.`, open: true },
    { q: 'can i cancel anytime?', a: 'yes. one tap in settings. you keep premium until the end of the billing period.' },
    { q: 'do you train models on my chats?', a: 'no. your chats with f8 stay private. they’re used to make f8 helpful for you, not for anyone else.' },
    { q: 'student or low-income tier?', a: 'we don’t have one yet, but write to us. we’ve made exceptions before.' },
    { q: `what happens at day ${trialLength}?`, a: `nothing is deleted. f8 pauses the ppl beyond your ${cfg.freeLimit} most recent, briefs go back to those ${cfg.freeLimit}, and the timeline window returns to ${cfg.windowDays} days. start premium any time and it all picks back up.`, trialOnly: true },
  ];
}

/**
 * Pricing-card highlight rows — the voice figure is SERVED, never written.
 * Named to match the app's `pricingHighlights` (premiumContent.ts).
 */
export function pricingHighlights(voice: VoiceLabels): string[] {
  return [
    'f8 remembers everyone, forever',
    'briefs + nudges on every event',
    voice.highlight,
    'no ads',
  ];
}

/* ------------------------------------------------------------------ *
 * Contextual copy — kit sentences whose numbers are SERVED, not written.
 *
 * Each helper reproduces the kit line exactly when the live values match the
 * canvas (limit 5, window 30), and degrades by dropping the clause rather than
 * inventing a number when a value is not served.
 * ------------------------------------------------------------------ */

const NUM_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const numWord = (n: number): string => NUM_WORDS[n] ?? String(n);
const ordinal = (n: number): string => {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
};

/** atCap banner headline — kit: "f8 is remembering 5 ppl — the most it holds for free." */
export function atCapBannerTitle(limit: number | null): string {
  return limit
    ? `f8 is remembering ${limit} ppl — the most it holds for free.`
    : 'f8 is remembering as many ppl as free holds.';
}

/** atCap banner body — kit: "your five are safe and staying. adding a 6th is the one thing free can’t do — premium remembers everyone." */
export function atCapBannerBody(limit: number | null): string {
  return limit
    ? `your ${numWord(limit)} are safe and staying. adding a ${ordinal(limit + 1)} is the one thing free can’t do — premium remembers everyone.`
    : 'they are safe and staying. adding one more is the one thing free can’t do — premium remembers everyone.';
}

export const LAPSED_BANNER_TITLE = 'your premium ended — nothing was deleted';

/**
 * Lapsed banner body — kit: "f8 remembers the 5 ppl you’ve seen most
 * recently, 30 days at a time. the other 14 — and everything older — are
 * archived and waiting." 5 / 30 / 14 are all served (quota limit, memory
 * window, remembered − active).
 */
export function lapsedBannerBody(
  limit: number | null,
  windowDays: number | null,
  paused: number | null,
): string {
  const who = limit ? `the ${limit} ppl` : 'the ppl';
  const window = windowDays ? `, ${windowDays} days at a time` : '';
  const rest = paused && paused > 0
    ? `the other ${paused} — and everything older — are archived and waiting.`
    : 'everything older is archived and waiting.';
  return `f8 remembers ${who} you’ve seen most recently${window}. ${rest}`;
}

/**
 * Lapsed hero sub — kit: "f8 still has everything — 14 ppl are paused, not
 * lost. pick up where you left off."
 * The count phrase drops to a plain reassurance when nothing (or nothing
 * known) is paused, so the sentence still reads whole.
 */
export function lapsedHeroSub(paused: number | null): string {
  const middle = paused && paused > 0
    ? `${paused} ppl are paused, not lost.`
    : 'nothing is lost.';
  return `f8 still has everything — ${middle} pick up where you left off.`;
}

/** Lapsed hero proof line — kit: "14 ppl paused — waiting for you". Null = render no proof row. */
export function lapsedProofLine(paused: number | null): { strong: string; rest: string } | null {
  if (!paused || paused <= 0) return null;
  return { strong: `${paused} ppl paused`, rest: ' — waiting for you' };
}

export interface TrialPanelRow {
  title: string;
  sub: string;
  /** Dimmed avatar cluster size, when the count is known. */
  avatars?: number;
}

/**
 * "at day N, concretely" rows (PPL-358 — countdown, not doom).
 *
 * Kit rows carry 9 / 5 / 30 — the ppl that would pause, the free limit, and
 * the free window. Callers pass the quota's `freeLimit`/`freeMemoryWindowDays`
 * (PPL-383) — served to everyone, including the trialist this panel exists
 * for. A null still degrades to the kit's qualitative sentence: the window
 * field is null while `rememberedGate` is off, and an old server serves
 * neither field.
 */
export function trialPanelRows(
  limit: number | null,
  windowDays: number | null,
  rememberedCount: number | null,
): TrialPanelRow[] {
  const beyond = limit !== null && rememberedCount !== null
    ? Math.max(0, rememberedCount - limit)
    : null;
  const rows: TrialPanelRow[] = [];

  if (beyond === null || beyond > 0) {
    rows.push({
      title: beyond !== null && limit !== null
        ? `the ${beyond} ppl beyond your ${limit} most recent pause`
        : limit !== null
          ? `the ppl beyond your ${limit} most recent pause`
          : 'the ppl beyond your most recent few pause',
      sub: 'paused, not forgotten — their stories stay archived.',
      ...(beyond !== null && beyond > 0 ? { avatars: beyond } : {}),
    });
  }
  rows.push({
    title: limit !== null ? `briefs go back to your ${limit}` : 'briefs go back to your most recent few',
    sub: 'events with anyone else arrive without one.',
  });
  rows.push({
    title: 'nudges stop',
    sub: 'f8 goes back to waiting to be asked.',
  });
  rows.push({
    title: windowDays !== null
      ? `the timeline returns to ${windowDays} days at a time`
      : 'the timeline stops holding everything forever',
    sub: 'everything older is archived, out of f8’s working reach.',
  });
  return rows;
}

/** Trial-panel footer — kit: "nothing is deleted at day 14. nothing is ever deleted." */
export function trialPanelFoot(trialLength: number): string {
  return `nothing is deleted at day ${trialLength}. nothing is ever deleted.`;
}

/** CTA label by page state (kit, with the trialing label re-registered by PPL-482). */
export function ctaLabel(state: 'default' | 'atCap' | 'trialing' | 'lapsed'): string {
  return state === 'trialing' ? 'remember everyone' : state === 'lapsed' ? 'restart premium' : 'start premium';
}
